/**
 * @file Admin Users Page — /cx/usuarios
 */
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Search, Eye } from "lucide-react";
import { useAdmin } from "../hooks/useAdmin";
import { useAuth } from "../hooks/useAuth";
import { ADMIN_STRINGS } from "../constants/strings";
import {
  Button,
  Card,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "../ui";
import "./AdminUsers.css";

const ROLES = ["PACIENTE", "PROFESIONAL", "ADMIN"];

export default function AdminUsers() {
  const { user: me } = useAuth();
  const { users, isLoading, refresh, changeRole, toggleActive } = useAdmin();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const handleSearch = () =>
    refresh({ search: search || undefined, role: roleFilter || undefined });

  const handleClear = () => {
    setSearch("");
    setRoleFilter("");
    refresh({});
  };

  const hasFilter = search || roleFilter;

  return (
    <div className="aux-page">
      <header className="aux-head">
        <span className="aux-eyebrow">Admin · Usuarios</span>
        <h1 className="aux-title">{ADMIN_STRINGS.USERS_TITLE}</h1>
      </header>

      <div className="aux-filters">
        <Input
          placeholder={ADMIN_STRINGS.SEARCH_PLACEHOLDER}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          leftIcon={<Search size={14} />}
        />
        <Select
          label={ADMIN_STRINGS.FILTER_ROLE}
          value={roleFilter}
          onValueChange={(v) => setRoleFilter(v === "__all__" ? "" : v)}
          placeholder={ADMIN_STRINGS.ALL_ROLES}
        >
          <SelectItem value="__all__">{ADMIN_STRINGS.ALL_ROLES}</SelectItem>
          {ROLES.map((r) => (
            <SelectItem key={r} value={r}>
              {ADMIN_STRINGS.ROLE_LABELS[r]}
            </SelectItem>
          ))}
        </Select>
        <div className="aux-filter-actions">
          <Button onClick={handleSearch}>Buscar</Button>
          {hasFilter && (
            <Button variant="ghost" onClick={handleClear}>
              Limpiar
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="aux-loading">
          <Spinner size={28} />
        </div>
      ) : users.length === 0 ? (
        <Card className="aux-empty">
          <p>{ADMIN_STRINGS.EMPTY}</p>
        </Card>
      ) : (
        <Card padded={false} className="aux-table-wrap">
          <Table>
            <THead>
              <TR>
                <TH>Nombre</TH>
                <TH>Contacto</TH>
                <TH>Rol</TH>
                <TH>Estado</TH>
                <TH>Registro</TH>
                <TH align="right">Acciones</TH>
              </TR>
            </THead>
            <TBody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <TR key={u.id} className={!u.isActive ? "aux-row-inactive" : ""}>
                    <TD>{u.firstName} {u.lastName}</TD>
                    <TD>
                      <div className="aux-contact">
                        <span>{u.email || "—"}</span>
                        {u.phone && <span className="aux-contact-sub">{u.phone}</span>}
                      </div>
                    </TD>
                    <TD>
                      <Select
                        value={u.role}
                        onValueChange={(v) => !isSelf && changeRole(u.id, v)}
                        disabled={isSelf}
                      >
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ADMIN_STRINGS.ROLE_LABELS[r]}
                          </SelectItem>
                        ))}
                      </Select>
                    </TD>
                    <TD>
                      <Chip tone={u.isActive ? "ok" : "neutral"} size="sm">
                        {u.isActive ? ADMIN_STRINGS.ACTIVE : ADMIN_STRINGS.INACTIVE}
                      </Chip>
                    </TD>
                    <TD>
                      <span className="aux-date">
                        {new Date(u.createdAt).toLocaleDateString("es-MX")}
                      </span>
                    </TD>
                    <TD align="right">
                      <div className="aux-actions">
                        <Button asChild variant="outline" size="sm">
                          <RouterLink to={`/cx/usuario/${u.id}`} data-testid="user-view-btn">
                            <Eye size={14} />
                          </RouterLink>
                        </Button>
                        <Button
                          variant={u.isActive ? "outline" : "primary"}
                          size="sm"
                          disabled={isSelf}
                          onClick={() => toggleActive(u.id)}
                        >
                          {u.isActive ? ADMIN_STRINGS.DEACTIVATE : ADMIN_STRINGS.ACTIVATE}
                        </Button>
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
